import React from "react";
import Tooltip from "@mui/material/Tooltip";
import TimelineIcon from "@mui/icons-material/Timeline";

function TimecardHistoryTooltip(props) {
  // Function to normalize status display names
  const normalizeStatusDisplay = (status) => {
    if (!status) return "None";

    // For employee view, use simplified statuses
    const statusMap = props.permissionRole === "employee" ? {
      'manual_approved': 'Approved',
      'manual_denied': 'Denied',
      'auto_approved': 'Approved',  // Simplified for employees
      'auto_flag': 'Pending',  // Simplified for employees
      'pending': 'Pending',
      'approved': 'Approved',
      'denied': 'Denied',
      'pay_period_published': 'Published',
    } : {
      'manual_approved': 'Approved',
      'manual_denied': 'Denied',
      'auto_approved': 'Auto-Approved',  // Keep distinct for manager timeline display
      'auto_flag': 'Flagged',
      'pending': 'Pending',
      'approved': 'Approved',
      'denied': 'Denied',
      'pay_period_published': 'Published',
    };

    return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Function to get role name from role_id
  const getRoleName = (roleId) => {
    if (!roleId || !props.rolesList) return roleId;
    const role = props.rolesList.find(r => r.id === roleId);
    return role ? role.role_name : roleId;
  };

  // Optional timeZone: the custom range report passes the store's so the
  // timeline matches its row; the time entries table omits it and uses
  // browser time, like its clock column.
  const zone = props.timeZone ? { timeZone: props.timeZone } : {};

  // Function to convert Unix timestamp to readable format
  const formatTimestamp = (unixTimestamp) => {
    const date = new Date(unixTimestamp * 1000);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...zone,
    });
  };

  // Function to format time from Unix timestamp to just time
  const formatTime = (unixTimestamp) => {
    if (!unixTimestamp) return null;
    const date = new Date(unixTimestamp * 1000);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...zone,
    });
  };

  // Function to extract breaks from pgh_context
  const extractBreaksFromContext = (pghContext) => {
    if (!pghContext) return [];

    const breaks = [];

    // Check for break_json (from clock in/out system)
    if (pghContext.break_json && Array.isArray(pghContext.break_json)) {
      breaks.push(...pghContext.break_json);
    }

    // Check for break_summary_json (from manager posts/edits)
    if (pghContext.break_summary_json && Array.isArray(pghContext.break_summary_json)) {
      breaks.push(...pghContext.break_summary_json);
    }

    return breaks;
  };

  // True when the record's context carries a break list (clock-out break_json
  // or a manager save's break_summary_json), even an empty one.
  const hasBreakContext = (record) =>
    Array.isArray(record?.pgh_context?.break_json) ||
    Array.isArray(record?.pgh_context?.break_summary_json);

  const sortedBreaks = (record) =>
    [...extractBreaksFromContext(record?.pgh_context)].sort(
      (a, b) => (a.clocked_in_at || 0) - (b.clocked_in_at || 0)
    );

  // Break times for display, in chronological order. withDate adds the day,
  // for edits that moved breaks to another date at the same clock times.
  const breakTimeList = (record, withDate = false) =>
    formatBreakTimes(sortedBreaks(record), withDate) || [];

  // Comparison key: real timestamps to the minute plus paid flag, so a move
  // to another day still registers even when the displayed times match.
  const toEpochMinute = (value) => {
    if (!value) return null;
    const seconds = value > 10000000000 ? value / 1000 : value;
    return Math.floor(seconds / 60);
  };
  const breakKey = (record) =>
    sortedBreaks(record)
      .map(b => `${toEpochMinute(b.clocked_in_at)}-${toEpochMinute(b.clocked_out_at)}-${Boolean(b.is_paid)}`)
      .join("|");

  // Helper to calculate minutes from a break object
  const getBreakMinutes = (b) => {
    let clockedIn = b.clocked_in_at;
    let clockedOut = b.clocked_out_at;
    if (clockedIn > 10000000000) clockedIn = Math.floor(clockedIn / 1000);
    if (clockedOut > 10000000000) clockedOut = Math.floor(clockedOut / 1000);
    if (clockedIn && clockedOut && clockedIn > 0 && clockedOut > 0 && clockedOut > clockedIn) {
      return Math.floor((clockedOut - clockedIn) / 60);
    }
    return 0;
  };

  // Function to calculate break time in minutes from a record
  const calculateTotalBreakTime = (record) => {
    const breaks = extractBreaksFromContext(record.pgh_context);
    if (breaks.length === 0) return null;

    const unpaidMinutes = breaks.filter(b => !b.is_paid).reduce((sum, b) => sum + getBreakMinutes(b), 0);
    const paidMinutes = breaks.filter(b => b.is_paid).reduce((sum, b) => sum + getBreakMinutes(b), 0);

    // Only return if we have a valid positive total
    if (unpaidMinutes === 0 && paidMinutes === 0) return null;
    return { unpaidMinutes, paidMinutes };
  };

  // Format break time object into display string(s)
  const formatBreakTimeSummary = (breakTimeObj) => {
    if (!breakTimeObj) return null;
    const { unpaidMinutes, paidMinutes } = breakTimeObj;
    if (unpaidMinutes > 0 && paidMinutes > 0) {
      return `${unpaidMinutes} Min (unpaid), ${paidMinutes} Min (paid)`;
    } else if (paidMinutes > 0) {
      return `${paidMinutes} Min (paid)`;
    } else {
      return `${unpaidMinutes} Min`;
    }
  };

  const extractBreakAttestations = (record) => {
    if (!record) return [];

    if (Array.isArray(record.break_attestations)) {
      return record.break_attestations;
    }

    if (record.pgh_context?.break_attestations && Array.isArray(record.pgh_context.break_attestations)) {
      return record.pgh_context.break_attestations;
    }

    return [];
  };

  const formatAttestationStatus = (status) => {
    if (status === "taken") return "Taken";
    if (status === "not_taken") return "Not taken";
    return status || "Unknown";
  };

  // Function to determine action type based on context
  const determineAction = (current, category, isFirstEntry) => {
    // For first entry, check if it was created by manager or employee
    if (isFirstEntry) {
      if (category === "manager_post") {
        return "Created by Manager";
      } else if (category === "clock_in") {
        return "Clocked In";
      }
      return "Shift Started";
    }

    // If it's a clock_out action, this represents a completed shift
    if (category === "clock_out") return "Shift Completed";

    // If it's a clock_in action
    if (category === "clock_in") return "Clocked In";

    // If it's a manager post, check what changed
    if (category === "manager_post") return "Edited";

    // If it's a manager post status change
    if (category === "manager_post_status") return "Status Changed";

    // Break actions
    if (category === "break_clock_in") return "Break Started";
    if (category === "break_clock_out") return "Break Ended";

    return "Updated";
  };

  // Function to format individual break times
  const formatBreakTimes = (breaks, withDate = false) => {
    if (!breaks || breaks.length === 0) return null;

    return breaks.map(b => {
      // Handle both timestamp formats (seconds and milliseconds)
      let clockedIn = b.clocked_in_at;
      let clockedOut = b.clocked_out_at;

      // Convert milliseconds to seconds if needed
      if (clockedIn > 10000000000) clockedIn = Math.floor(clockedIn / 1000);
      if (clockedOut > 10000000000) clockedOut = Math.floor(clockedOut / 1000);

      if (clockedIn && clockedOut && clockedIn > 0 && clockedOut > 0) {
        const label = b.is_paid ? " (paid)" : "";
        const day = withDate
          ? `${new Date(clockedIn * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", ...zone })}, `
          : "";
        return `${day}${formatTime(clockedIn)} - ${formatTime(clockedOut)}${label}`;
      }
      return null;
    }).filter(b => b !== null);
  };

  // Function to detect changes between two history items
  const detectChanges = (current, previous, isFirstEntry, isShiftCompleted, breakBaseline) => {
    const changes = {};
    const category = current.pgh_context?.category;
    const attestationDetails = extractBreakAttestations(current);
    const attestationNote = current.break_attestation_note || current.pgh_context?.break_attestation_note;


    // For initial creation (clock in or manager creation)
    if (isFirstEntry) {
      if (current.clocked_in_at) {
        changes.clockIn = formatTime(current.clocked_in_at);
      }
      // Only show clock out if it exists at creation (manager created complete shift)
      if (current.clocked_out_at) {
        changes.clockOut = formatTime(current.clocked_out_at);
      }

      // Only add break time if shift is complete at creation
      if (current.clocked_out_at) {
        const breakTimes = breakTimeList(current);
        if (breakTimes.length > 0) {
          changes.breaks = breakTimes;
        }
        const breakTimeSummary = formatBreakTimeSummary(calculateTotalBreakTime(current));
        if (breakTimeSummary) {
          changes.breakTime = breakTimeSummary;
        }
      }

      // Add role_id for first entry
      if (current.role_id) {
        changes.roleId = getRoleName(current.role_id);
      }

      // Add manager note if exists
      if (current.manager_note) {
        changes.managerNote = current.manager_note;
      }

      return changes;
    }

    // For shift completed
    if (isShiftCompleted) {
      if (current.clocked_in_at) {
        changes.clockIn = formatTime(current.clocked_in_at);
      }
      if (current.clocked_out_at) {
        changes.clockOut = formatTime(current.clocked_out_at);
      }

      // Add individual break times for completed shift
      const breaks = extractBreaksFromContext(current.pgh_context);
      if (breaks.length > 0) {
        const breakTimes = formatBreakTimes(breaks);
        if (breakTimes.length > 0) {
          changes.breaks = breakTimes;
        }
      }

      // Add total break time for completed shift
      const breakTimeSummary = formatBreakTimeSummary(calculateTotalBreakTime(current));
      if (breakTimeSummary) {
        changes.breakTime = breakTimeSummary;
      }

      if (attestationDetails.length > 0) {
        changes.breakAttestations = attestationDetails.map((attestation, idx) =>
          `Break ${idx + 1}: ${formatAttestationStatus(attestation.attestation_status)}`
        );
      }

      if (attestationNote) {
        changes.breakAttestationNote = attestationNote;
      }

      // Add paid breaks confirmation if available (check both locations)
      const paidBreaksTaken = current.paid_breaks_taken ?? current.pgh_context?.paid_breaks_taken;
      if (paidBreaksTaken !== null && typeof paidBreaksTaken !== 'undefined') {
        changes.paidBreaksConfirmation = paidBreaksTaken
          ? "Yes, took scheduled paid break(s)"
          : "No, did not take scheduled paid break(s)";
      }

      // Add meal break confirmation if available (check both locations)
      const mealBreakTaken = current.meal_break_taken ?? current.pgh_context?.meal_break_taken;
      if (mealBreakTaken !== null && typeof mealBreakTaken !== 'undefined') {
        changes.mealBreakConfirmation = mealBreakTaken
          ? "Yes, took scheduled meal break"
          : "No, did not take scheduled meal break";
      }

      return changes;
    }

    // For edits, show what changed
    if (previous) {
      // Check clock in changes
      if (current.clocked_in_at !== previous.clocked_in_at) {
        changes.clockIn = `${formatTime(previous.clocked_in_at)} → ${formatTime(current.clocked_in_at)}`;
      }

      // Check clock out changes
      if (current.clocked_out_at !== previous.clocked_out_at) {
        changes.clockOut = `${formatTime(previous.clocked_out_at)} → ${formatTime(current.clocked_out_at)}`;
      }

      // Check status changes
      if (current.status !== previous.status) {
        const prevStatus = normalizeStatusDisplay(previous.status);
        const currStatus = normalizeStatusDisplay(current.status);
        changes.status = `${prevStatus} → ${currStatus}`;
      }

      // Check role changes
      if (current.role_id !== previous.role_id) {
        changes.roleId = `${getRoleName(previous.role_id)} → ${getRoleName(current.role_id)}`;
      }

      // Check break changes against the last record that carried a break list
      // (the immediately previous event may be a status change without one).
      // Comparing timestamps, not totals or display strings, catches a break
      // that moved, including to another day at the same clock times.
      if (hasBreakContext(current) && breakKey(current) !== (breakBaseline ? breakKey(breakBaseline) : "")) {
        let prevBreakTimes = breakBaseline ? breakTimeList(breakBaseline) : [];
        let currBreakTimes = breakTimeList(current);
        if (prevBreakTimes.join("|") === currBreakTimes.join("|")) {
          prevBreakTimes = breakBaseline ? breakTimeList(breakBaseline, true) : [];
          currBreakTimes = breakTimeList(current, true);
        }
        changes.breaks = currBreakTimes.length > 0 ? currBreakTimes : ["None"];
        if (prevBreakTimes.length > 0) {
          changes.previousBreaks = prevBreakTimes;
        }
        const prevBreakTimeSummary = breakBaseline ? formatBreakTimeSummary(calculateTotalBreakTime(breakBaseline)) : null;
        const currBreakTimeSummary = formatBreakTimeSummary(calculateTotalBreakTime(current)) || "0 Min";
        if (prevBreakTimeSummary !== currBreakTimeSummary) {
          changes.breakTime = prevBreakTimeSummary
            ? `${prevBreakTimeSummary} → ${currBreakTimeSummary}`
            : currBreakTimeSummary;
        }
      }

      // Check manager note changes
      if (current.manager_note !== previous.manager_note && current.manager_note) {
        changes.managerNote = current.manager_note;
      }

      // Check shift note changes (break_attestation_note)
      const prevAttestationNote = previous.break_attestation_note || previous.pgh_context?.break_attestation_note;
      if (attestationNote !== prevAttestationNote && attestationNote) {
        changes.breakAttestationNote = attestationNote;
      }
    }

    if (attestationDetails.length > 0) {
      changes.breakAttestations = attestationDetails.map((attestation, idx) =>
        `Break ${idx + 1}: ${formatAttestationStatus(attestation.attestation_status)}`
      );
    }

    return changes;
  };

  // Main transformation function
  const transformHistoryToTimeline = (rawHistory) => {
    if (!rawHistory || rawHistory.length === 0) return [];

    // Sort by pgh_created_at in ascending order (oldest first)
    const sortedRecords = [...rawHistory].sort((a, b) => a.pgh_created_at - b.pgh_created_at);

    const timeline = [];
    let lastClockOutIndex = -1;
    let breakBaseline = null;

    sortedRecords.forEach((current, index) => {
      const category = current.pgh_context?.category;

      // Skip ALL timecard_break events - these are implementation details
      // Break changes are already captured in the Timecard's pgh_context.break_json
      // or pgh_context.break_summary_json and displayed in the "Shift Completed" entry
      if (current.record_type === "timecard_break") {
        return;
      }

      // For Timecard events, find the previous Timecard event (not TimecardBreak)
      // to ensure we're comparing the same record types
      let previous = null;
      if (current.record_type === "timecard" && index > 0) {
        // Find the most recent Timecard event before this one
        for (let i = index - 1; i >= 0; i--) {
          if (sortedRecords[i].record_type === "timecard") {
            previous = sortedRecords[i];
            break;
          }
        }
      } else if (index > 0) {
        // For non-Timecard events, use the immediately previous record
        previous = sortedRecords[index - 1];
      }

      // Determine if this should be a new timeline entry
      let shouldAddEntry = false;
      let action = "";
      let isFirstEntry = false;
      let isShiftCompleted = false;

      // Always add the first entry (clock in or manager creation)
      if (index === 0) {
        shouldAddEntry = true;
        isFirstEntry = true;
        action = determineAction(current, category, isFirstEntry);
      }
      // Add entry for clock out (represents completed shift)
      else if (category === "clock_out") {
        shouldAddEntry = true;
        action = "Shift Completed";
        isShiftCompleted = true;
        lastClockOutIndex = index;
      }
      // Add entry for manager edits after the shift is complete
      // Only show timeline entries for Timecard edits, not TimecardBreak edits
      else if (category === "manager_post" && current.record_type === "timecard" && index > lastClockOutIndex) {
        shouldAddEntry = true;
        action = determineAction(current, category, false);
      }
      // Add entry for manager status changes
      else if (category === "manager_post_status") {
        shouldAddEntry = true;
        // Normalize status values (manual_approved -> approved, manual_denied -> denied)
        const currentStatus = current.status === "manual_approved" ? "approved" : current.status === "manual_denied" ? "denied" : current.status;
        const previousStatus = previous?.status === "manual_approved" ? "approved" : previous?.status === "manual_denied" ? "denied" : previous?.status;

        if (currentStatus === "approved") action = "Approved";
        else if (currentStatus === "denied") action = "Denied";
        else if (previous && previousStatus === "approved" && currentStatus === "pending") action = "Reverted";
        else if (previous && previousStatus === "denied" && currentStatus === "pending") action = "Reverted";
        else action = "Status Changed";
      }
      // Add entry for status changes (legacy support)
      else if (previous && current.status !== previous.status && category !== "manager_post_status") {
        shouldAddEntry = true;
        // Normalize status values (manual_approved -> approved, manual_denied -> denied)
        const currentStatus = current.status === "manual_approved" ? "approved" : current.status === "manual_denied" ? "denied" : current.status;
        const previousStatus = previous.status === "manual_approved" ? "approved" : previous.status === "manual_denied" ? "denied" : previous.status;

        if (currentStatus === "approved") action = "Approved";
        else if (currentStatus === "denied") action = "Denied";
        else if (previousStatus === "approved" && currentStatus === "pending") action = "Reverted";
        else if (previousStatus === "denied" && currentStatus === "pending") action = "Reverted";
        else action = "Status Changed";
      }

      if (shouldAddEntry) {
        const changes = detectChanges(current, previous, isFirstEntry, isShiftCompleted, breakBaseline);

        // Only add if there are actual changes or it's a significant action
        if (Object.keys(changes).length > 0) {
          timeline.push({
            timestamp: formatTimestamp(current.pgh_created_at),
            action: action,
            user: current.pgh_context?.stm_name || "System",
            changes: changes
          });
        }
      }

      if (hasBreakContext(current)) {
        breakBaseline = current;
      }
    });

    return timeline;
  };

  // Check if we should show the timeline icon
  const shouldShowTimeline = () => {
    if (!props.history || props.history.length === 0) return false;

    const sortedRecords = [...props.history].sort((a, b) => a.pgh_created_at - b.pgh_created_at);
    const firstRecord = sortedRecords[0];

    // Only show if the first record has a pgh_context
    if (!firstRecord.pgh_context) return false;

    // Check if there's a clock out time - if not, the shift is still ongoing
    // Check the most recent record for clocked_out_at
    const mostRecentRecord = sortedRecords[sortedRecords.length - 1];
    if (!mostRecentRecord.clocked_out_at) return false;

    return true;
  };

  // Check if there's any manager post in the history
  const hasManagerPost = () => {
    if (!props.history || props.history.length === 0) return false;

    return props.history.some(record =>
      record.pgh_context?.category === "manager_post"
    );
  };

  // Don't render the icon if we shouldn't show the timeline
  if (!shouldShowTimeline()) {
    return null;
  }

  // Transform the history data
  const timelineData = transformHistoryToTimeline(props.history);

  console.log("HISTORY", props.history)

  const tooltipContent = (
    <div>
      <h4 className="text-slate-700 text-sm font-bold mb-3">
        TIME ENTRY HISTORY
      </h4>
      {timelineData.length === 0 ? (
        <p className="text-xs text-slate-500">No history available</p>
      ) : (
        <div className="space-y-4">
          {timelineData.map((item, index) => (
            <div key={index} className="relative">
              {/* Timeline connector line */}
              {index !== timelineData.length - 1 && (
                <div className="absolute left-1 top-6 bottom-0 w-0.5 bg-slate-200"></div>
              )}

              {/* Timeline entry */}
              <div className="flex items-start gap-3">
                {/* Timeline dot */}
                <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0 bg-violet-700"></div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-slate-900">
                      {item.action}
                    </p>
                    <p className="text-xxs text-slate-500">{item.timestamp}</p>
                  </div>
                  <p className="text-xxs text-slate-600 mb-1">by {item.user}</p>
                  {Object.keys(item.changes).length > 0 && (
                    <div className="text-xxs text-slate-500 space-y-0.5">
                      {Object.entries(item.changes).map(([key, value], i) => {
                        // Special handling for manager note - display prominently
                        if (key === 'managerNote') {
                          return (
                            <div key={i} className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                              <p className="font-semibold text-blue-900 mb-0.5">Note:</p>
                              <p className="text-blue-800" style={{ whiteSpace: 'pre-wrap' }}>
                                {value}
                              </p>
                            </div>
                          );
                        }

                        // Special handling for breaks array
                        if (key === 'breaks' && Array.isArray(value)) {
                          return (
                            <div key={i} className="flex items-start">
                              <span className="mr-1">•</span>
                              <div className="flex-1">
                                <span className="capitalize">Breaks:</span>
                                {value.map((breakTime, idx) => (
                                  <div key={idx} className="ml-2">
                                    {breakTime}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        if (key === 'previousBreaks' && Array.isArray(value)) {
                          return (
                            <div key={i} className="flex items-start">
                              <span className="mr-1">•</span>
                              <div className="flex-1">
                                <span>Previous breaks:</span>
                                {value.map((breakTime, idx) => (
                                  <div key={idx} className="ml-2 line-through">
                                    {breakTime}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        if (key === 'breakAttestations' && Array.isArray(value)) {
                          return (
                            <div key={i} className="flex items-start">
                              <span className="mr-1">•</span>
                              <div className="flex-1">
                                <span className="capitalize">Break Attestations:</span>
                                {value.map((attestation, idx) => (
                                  <div key={idx} className="ml-2">
                                    {attestation}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        // Special handling for paid breaks confirmation
                        if (key === 'paidBreaksConfirmation') {
                          return (
                            <div key={i} className="flex items-start">
                              <span className="mr-1">•</span>
                              <span>{value}</span>
                            </div>
                          );
                        }

                        if (key === 'mealBreakConfirmation') {
                          return (
                            <div key={i} className="flex items-start">
                              <span className="mr-1">•</span>
                              <span>{value}</span>
                            </div>
                          );
                        }

                        // Special handling for roleId to display as "Role"
                        // Special handling for breakAttestationNote to display as "Shift note"
                        const displayKey = key === 'roleId' ? 'Role' : key === 'breakAttestationNote' ? 'Shift note' : key.replace(/([A-Z])/g, " $1").trim();
                        return (
                          <div key={i} className="flex items-start">
                            <span className="mr-1">•</span>
                            <span className="capitalize">
                              {displayKey}: {value}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Determine icon color based on whether there's a manager post
  const iconColor = hasManagerPost() ? "text-violet-700" : "text-slate-400";
  const hoverColor = hasManagerPost() ? "hover:text-violet-900" : "hover:text-slate-600";

  return (
    <Tooltip
      title={tooltipContent}
      componentsProps={{
        tooltip: {
          sx: {
            maxHeight: "500px",
            overflow: "auto",
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "8px",
            boxShadow:
              "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
            border: "1px solid #e2e8f0",
            color: "inherit",
            width: "350px",
          },
        },
      }}
    >
      <TimelineIcon
        className={`${iconColor} ${hoverColor} cursor-pointer transition-colors`}
        style={{ fontSize: "16px" }}
      />
    </Tooltip>
  );
}

export default TimecardHistoryTooltip;
